import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { Metadata, RunView } from '@memberjunction/core';
import { Subject } from 'rxjs';
import { mjBizAppsCommonOrganizationEntity } from '@mj-biz-apps/common-entities';
import { IzzyIzzyActionOrganizationEntity, IzzyIzzyActionEntity, IzzyMetadataEngine } from 'mj_generatedentities';
import { IAdminPanel, AdminPanelStatus, DEFAULT_ADMIN_PANEL_STATUS } from '../admin-panel.interface';

interface GrantDisplay {
  grant: IzzyIzzyActionOrganizationEntity;
  action: IzzyIzzyActionEntity | null;
  organization: mjBizAppsCommonOrganizationEntity | null;
  isEditing: boolean;
  editForm: GrantEditForm;
}

interface GrantEditForm {
  izzyActionId: string;
  organizationId: string;
  status: 'Active' | 'Revoked';
  expiresAt: string;
  notes: string;
}

@Component({
  standalone: false,
  selector: 'izzy-private-action-grants',
  templateUrl: './private-action-grants.component.html',
  styleUrls: ['./private-action-grants.component.css']
})
export class PrivateActionGrantsComponent implements OnInit, OnDestroy, IAdminPanel {
  @Input() organizationId: string | null = null;
  @Input() initialState: any = null;
  @Output() stateChange = new EventEmitter<any>();
  @Output() statusChange = new EventEmitter<AdminPanelStatus>();
  @Output() interaction = new EventEmitter<any>();

  constructor() {}

  protected grants: IzzyIzzyActionOrganizationEntity[] = [];
  protected grantDisplays: GrantDisplay[] = [];
  protected actions: IzzyIzzyActionEntity[] = [];
  protected privateActions: IzzyIzzyActionEntity[] = [];
  protected organizations: mjBizAppsCommonOrganizationEntity[] = [];

  protected isLoading: boolean = true;
  protected isSaving: boolean = false;
  protected errorMessage: string = '';
  protected successMessage: string = '';
  protected searchQuery: string = '';
  protected statusFilter: string = 'all';

  protected isCreating: boolean = false;
  protected createForm: GrantEditForm = this.getEmptyForm();

  protected confirmDialogVisible: boolean = false;
  protected confirmDialogTitle: string = '';
  protected confirmDialogMessage: string = '';
  protected confirmDialogAction: (() => Promise<void>) | null = null;

  private destroy$ = new Subject<void>();

  async ngOnInit(): Promise<void> {
    await this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getEmptyForm(): GrantEditForm {
    return {
      izzyActionId: '',
      organizationId: this.organizationId || '',
      status: 'Active',
      expiresAt: '',
      notes: ''
    };
  }

  protected async loadData(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';

    try {
      await IzzyMetadataEngine.Instance.Config(false);
      this.organizations = IzzyMetadataEngine.Instance.GetCurrentUserOrganizations();

      // Load all Izzy actions
      const rvActions = new RunView();
      const actionsResult = await rvActions.RunView<IzzyIzzyActionEntity>({
        EntityName: 'Izzy Actions',
        OrderBy: 'Name ASC'
      });
      this.actions = actionsResult.Results;
      this.privateActions = this.actions.filter(a => !a.IsPublic);

      // Load grants
      let extraFilter = '';
      if (this.organizationId) {
        extraFilter = `OrganizationID = '${this.organizationId}'`;
      }
      const rv = new RunView();
      const result = await rv.RunView<IzzyIzzyActionOrganizationEntity>({
        EntityName: 'Izzy Action Organizations',
        ExtraFilter: extraFilter || undefined,
        OrderBy: 'GrantedAt DESC'
      });
      this.grants = result.Results;
      this.grantDisplays = this.grants.map(g => this.createDisplay(g));

      // Emit default status (private action grants don't have a health indicator)
      this.statusChange.emit(DEFAULT_ADMIN_PANEL_STATUS);
    } catch (error) {
      console.error('Error loading grants:', error);
      this.errorMessage = 'Failed to load private action grants.';
    } finally {
      this.isLoading = false;
    }
  }

  private createDisplay(grant: IzzyIzzyActionOrganizationEntity): GrantDisplay {
    const action = this.actions.find(a => a.ID === grant.IzzyActionID) || null;
    const organization = this.organizations.find(o => o.ID === grant.OrganizationID) || null;
    return {
      grant,
      action,
      organization,
      isEditing: false,
      editForm: this.grantToForm(grant)
    };
  }

  private grantToForm(grant: IzzyIzzyActionOrganizationEntity): GrantEditForm {
    return {
      izzyActionId: grant.IzzyActionID,
      organizationId: grant.OrganizationID,
      status: grant.Status as 'Active' | 'Revoked',
      expiresAt: grant.ExpiresAt ? this.formatDateForInput(grant.ExpiresAt) : '',
      notes: grant.Notes || ''
    };
  }

  private formatDateForInput(date: Date | string): string {
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  }

  get filteredGrants(): GrantDisplay[] {
    return this.grantDisplays.filter(gd => {
      const actionName = gd.action?.Name || '';
      const orgName = gd.organization?.Name || '';
      const matchesSearch = !this.searchQuery.trim() ||
        actionName.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        orgName.toLowerCase().includes(this.searchQuery.toLowerCase());
      const matchesStatus = this.statusFilter === 'all' || gd.grant.Status === this.statusFilter;
      return matchesSearch && matchesStatus;
    });
  }

  get availableActions(): IzzyIzzyActionEntity[] {
    // For new grants, show actions not already granted to the selected org
    const orgId = this.createForm.organizationId;
    if (!orgId) return this.privateActions;

    const grantedActionIds = new Set(
      this.grants
        .filter(g => g.OrganizationID === orgId && g.Status !== 'Revoked')
        .map(g => g.IzzyActionID)
    );
    return this.privateActions.filter(a => !grantedActionIds.has(a.ID));
  }

  startCreate(): void {
    this.isCreating = true;
    this.createForm = this.getEmptyForm();
    if (this.organizations.length > 0 && !this.createForm.organizationId) {
      this.createForm.organizationId = this.organizations[0].ID;
    }
  }

  cancelCreate(): void {
    this.isCreating = false;
    this.createForm = this.getEmptyForm();
  }

  async saveCreate(): Promise<void> {
    if (!this.createForm.izzyActionId || !this.createForm.organizationId) {
      this.errorMessage = 'Please select an action and organization.';
      return;
    }

    this.isSaving = true;
    try {
      const md = new Metadata();
      const newGrant = await md.GetEntityObject<IzzyIzzyActionOrganizationEntity>('Izzy Action Organizations');
      newGrant.IzzyActionID = this.createForm.izzyActionId;
      newGrant.OrganizationID = this.createForm.organizationId;
      newGrant.Status = this.createForm.status;
      newGrant.GrantedAt = new Date();
      newGrant.ExpiresAt = this.createForm.expiresAt ? new Date(this.createForm.expiresAt) : null;
      newGrant.Notes = this.createForm.notes.trim() || null;

      await newGrant.Save();
      await this.loadData();
      this.isCreating = false;
      this.showSuccess('Action grant created');
    } catch (error) {
      console.error('Error creating grant:', error);
      this.errorMessage = 'Failed to create grant.';
    } finally {
      this.isSaving = false;
    }
  }

  startEdit(display: GrantDisplay): void {
    display.isEditing = true;
    display.editForm = this.grantToForm(display.grant);
  }

  cancelEdit(display: GrantDisplay): void {
    display.isEditing = false;
  }

  async saveEdit(display: GrantDisplay): Promise<void> {
    this.isSaving = true;
    try {
      display.grant.Status = display.editForm.status;
      display.grant.ExpiresAt = display.editForm.expiresAt ? new Date(display.editForm.expiresAt) : null;
      display.grant.Notes = display.editForm.notes.trim() || null;

      await display.grant.Save();
      display.isEditing = false;
      this.showSuccess('Grant updated');
    } catch (error) {
      console.error('Error updating grant:', error);
      this.errorMessage = 'Failed to update grant.';
    } finally {
      this.isSaving = false;
    }
  }

  confirmRevoke(display: GrantDisplay): void {
    const actionName = display.action?.Name || 'this action';
    const orgName = display.organization?.Name || 'this organization';
    this.showConfirmDialog(
      'Revoke Action Grant',
      `Are you sure you want to revoke access to "${actionName}" for ${orgName}?`,
      async () => {
        this.isSaving = true;
        try {
          await display.grant.Delete();
          await this.loadData();
          this.showSuccess('Grant revoked');
        } catch (error) {
          console.error('Error revoking grant:', error);
          this.errorMessage = 'Failed to revoke grant.';
        } finally {
          this.isSaving = false;
        }
      }
    );
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Active': return 'status-active';
      case 'Pending': return 'status-pending';
      case 'Revoked': return 'status-revoked';
      default: return '';
    }
  }

  formatDate(date: Date | string | null): string {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleDateString();
  }

  isExpired(grant: IzzyIzzyActionOrganizationEntity): boolean {
    if (!grant.ExpiresAt) return false;
    return new Date(grant.ExpiresAt) < new Date();
  }

  protected showConfirmDialog(title: string, message: string, action: () => Promise<void>): void {
    this.confirmDialogTitle = title;
    this.confirmDialogMessage = message;
    this.confirmDialogAction = action;
    this.confirmDialogVisible = true;
  }

  protected async confirmDialogYes(): Promise<void> {
    if (this.confirmDialogAction) {
      await this.confirmDialogAction();
    }
    this.closeConfirmDialog();
  }

  protected closeConfirmDialog(): void {
    this.confirmDialogVisible = false;
    this.confirmDialogAction = null;
  }

  private showSuccess(message: string): void {
    this.successMessage = message;
    setTimeout(() => { this.successMessage = ''; }, 3000);
  }
}

export function LoadPrivateActionGrantsComponent(): void {}
